import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Edit as EditIcon,
  VerticalSplit as SplitIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import MarkdownPreview from './MarkdownPreview';
import { useNotesStore } from '../../store/notes';

type ViewMode = 'edit' | 'preview' | 'split';

const AUTOSAVE_DELAY = 2000;

interface NoteEditorProps {
  isMobile?: boolean;
  loading?: boolean;
  onBack?: () => void;
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  isMobile = false,
  loading = false,
  onBack,
}) => {
  const { activeNote, saving, updateNote } = useNotesStore();
  const [mode, setMode] = useState<ViewMode>(isMobile ? 'edit' : 'split');
  const [localContent, setLocalContent] = useState('');
  const [localTitle, setLocalTitle] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync from store → local state when a new note is opened
  useEffect(() => {
    if (activeNote) {
      setLocalContent(activeNote.content);
      setLocalTitle(activeNote.title);
    }
  }, [activeNote?.id]);

  const scheduleSave = useCallback(
    (content: string, title?: string) => {
      if (!activeNote) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        updateNote(activeNote.id, content, title);
      }, AUTOSAVE_DELAY);
    },
    [activeNote, updateNote]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isMobile && mode === 'split') {
      setMode('edit');
    }
  }, [isMobile, mode]);

  const handleContentChange = (value: string) => {
    setLocalContent(value);
    scheduleSave(value, localTitle);
  };

  const handleTitleChange = (value: string) => {
    setLocalTitle(value);
    scheduleSave(localContent, value);
  };

  if (!activeNote) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          p: 3,
        }}
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <Typography variant="body1" align="center">
            Select a note or create a new one
          </Typography>
        )}
      </Box>
    );
  }

  const editorPane = (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
        minHeight: 0,
        position: 'relative',
      }}
    >
      <Box
        component="textarea"
        value={localContent}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          handleContentChange(e.target.value)
        }
        placeholder="Start writing..."
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          resize: 'none',
          border: 'none',
          outline: 'none',
          p: { xs: 1.5, md: 2 },
          fontFamily: 'monospace',
          fontSize: { xs: '1rem', md: '0.95rem' },
          lineHeight: 1.6,
          bgcolor: 'background.paper',
          color: 'text.primary',
          overflow: 'auto',
          boxSizing: 'border-box',
          WebkitOverflowScrolling: 'touch',
        }}
      />
    </Box>
  );

  const previewPane = <MarkdownPreview content={localContent} />;
  const showSplit = !isMobile;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          px: { xs: 1.5, md: 2 },
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {isMobile && onBack && (
            <IconButton
              onClick={onBack}
              aria-label="Back to notes list"
              size="small"
              sx={{ width: 44, height: 44, flexShrink: 0 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Box
            component="input"
            value={localTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleTitleChange(e.target.value)
            }
            placeholder="Untitled"
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 44, md: 0 },
              border: 'none',
              outline: 'none',
              py: { xs: 1, md: 0 },
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 600,
              lineHeight: 1.3,
              bgcolor: 'transparent',
              color: 'text.primary',
            }}
          />
          {saving && <CircularProgress size={16} sx={{ flexShrink: 0 }} />}
        </Box>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_e, v) => v && setMode(v as ViewMode)}
          size="small"
          fullWidth={isMobile}
          sx={{
            flexShrink: 0,
            '& .MuiToggleButton-root': {
              minHeight: { xs: 44, md: 32 },
            },
          }}
        >
          <ToggleButton value="edit" title="Edit">
            <EditIcon fontSize="small" />
          </ToggleButton>
          {showSplit && (
            <ToggleButton value="split" title="Split">
              <SplitIcon fontSize="small" />
            </ToggleButton>
          )}
          <ToggleButton value="preview" title="Preview">
            <PreviewIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {mode === 'edit' && editorPane}
        {mode === 'preview' && previewPane}
        {mode === 'split' && showSplit && (
          <>
            {editorPane}
            <Box sx={{ width: '1px', flexShrink: 0, bgcolor: 'divider' }} />
            {previewPane}
          </>
        )}
      </Box>
    </Box>
  );
};

export default NoteEditor;
